import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText,
  Plane,
  Download,
  CheckCircle,
  Clock,
  AlertTriangle,
  User,
  Users,
  Building2,
  Receipt,
  ArrowLeft,
  Edit,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelRequestDetail = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);

  useEffect(() => {
    fetchRequestDetail();
  }, [id]);

  const fetchRequestDetail = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/requests/${id}`, { withCredentials: true });
      setRequest(data.request);
    } catch (err) {
      toast.error('Failed to load travel request details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      const response = await axios.get(`${API_URL}/travel-desk/requests/${id}/receipt`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TravelReceipt_${request?.request_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Travel Approval Receipt downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download receipt PDF');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[500px]">
        <div className="text-slate-500 font-semibold">Loading Travel Request Details...</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-8 text-center space-y-4">
        <p className="text-rose-600 font-bold">Travel request not found.</p>
        <button onClick={() => navigate('/travel/requests')} className="px-4 py-2 bg-slate-900 text-white font-bold text-xs rounded-xl">
          Back to Travel Requests
        </button>
      </div>
    );
  }

  const isReceiptEligible = ['approved', 'booked', 'completed', 'finance_approved'].includes(request.status);
  const isEditable = ['draft', 'submitted', 'pending_approval', 'returned_for_correction'].includes(request.status);

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto font-sans">
      {/* Header Bar */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/travel/requests')}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <span className="font-mono text-xs font-bold text-amber-400">{request.request_number}</span>
              <h1 className="text-xl font-black tracking-tight">{request.employee_name} — {request.purpose}</h1>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isReceiptEligible && hasPermission(user, 'travel_desk.request_detail.view') && (
            <button
              onClick={handleDownloadReceipt}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-md flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Download Approval Receipt
            </button>
          )}

          {isEditable && hasPermission(user, 'travel_desk.edit_request.edit') && (
            <button
              onClick={() => navigate(`/travel/request/${id}/edit`)}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-1.5"
            >
              <Edit className="w-4 h-4" /> Edit Request
            </button>
          )}
        </div>
      </div>

      {/* Main Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Requisition Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Employee & Travel Overview */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600" /> Employee &amp; Requisition Overview
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
              <div>
                <span className="text-slate-400 font-medium block">Employee Name</span>
                <span className="font-bold text-slate-900">{request.employee_name}</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Department</span>
                <span className="font-bold text-slate-900">{request.department}</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Designation / Grade</span>
                <span className="font-bold text-slate-900">{request.designation} ({request.grade})</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Travel Mode</span>
                <span className="font-bold capitalize text-blue-600">{request.travel_type}</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Category</span>
                <span className="font-bold text-slate-900">{request.travel_category || 'Domestic'}</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Est. Budget</span>
                <span className="font-mono font-bold text-emerald-600">₹ {(request.estimated_budget || 0).toLocaleString('en-IN')}</span>
              </div>
            </div>

            {request.policy_warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl text-xs text-amber-900 flex items-center gap-2 font-medium">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Policy Warnings: {request.policy_warnings.join(' | ')}</span>
              </div>
            )}
          </div>

          {/* Journey Legs */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-3 flex items-center gap-2">
              <Plane className="w-4 h-4 text-blue-600" /> Route &amp; Journey Manifest
            </h3>

            <div className="space-y-3">
              {(request.journey_details || []).map((leg, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-xl border border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <span className="text-slate-400 font-medium block">From</span>
                    <span className="font-bold text-slate-900">{leg.from_city}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-medium block">To</span>
                    <span className="font-bold text-slate-900">{leg.to_city}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-medium block">Departure Date</span>
                    <span className="font-bold text-slate-900">{leg.departure_date}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-medium block">Return Date</span>
                    <span className="font-bold text-slate-900">{leg.return_date || 'One Way'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Passengers */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" /> Passenger Details
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 font-bold uppercase text-[10px] text-slate-600">
                  <tr>
                    <th className="p-2.5">Passenger Name</th>
                    <th className="p-2.5">Age</th>
                    <th className="p-2.5">Gender</th>
                    <th className="p-2.5">ID Number</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium">
                  {(request.passengers || []).map((p, i) => (
                    <tr key={i}>
                      <td className="p-2.5 font-bold">{p.name}</td>
                      <td className="p-2.5">{p.age}</td>
                      <td className="p-2.5">{p.gender}</td>
                      <td className="p-2.5 font-mono">{p.id_number || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column: Workflow & Timeline */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" /> Approval Workflow Timeline
            </h3>

            <div className="space-y-3">
              {(request.approval_timeline || []).map((evt, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-1 text-xs">
                  <div className="flex justify-between items-center font-bold text-slate-800">
                    <span className="capitalize">{evt.step?.replace(/_/g, ' ')}</span>
                    <span className="text-[10px] text-emerald-700 font-mono uppercase font-black">{evt.action}</span>
                  </div>
                  <div className="text-slate-500 text-[11px]">By: {evt.actor_name} ({evt.actor_role})</div>
                  {evt.comments && <div className="text-slate-600 italic text-[11px]">"{evt.comments}"</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelRequestDetail;
